import type { Equation } from "@src/features/equation-editor/equation";
import {
  closePopupMenu,
  focusOpenedPopupMenu,
  handlePopupMenuItemFocus,
  handlePopupMenuKeyDown,
  handlePopupMenuTriggerKeyDown,
} from "../lib/popup-menu";
import { DiagnosticBadge } from "./cards/DiagnosticField";
import { MAX_EQUATION_NODES } from "@src/features/equation-editor/equation-policy";
import type { MatrixPreset } from "@src/features/equation-editor/presets";
import type { Translate } from "@src/i18n/translate";
import {
  analyzeEquationCapabilities,
  type EquationCapabilities,
} from "@src/features/equation-editor/capability-analysis";
import { canAddEquationNodes } from "@src/features/equation-editor/equation-operations";
import { getMatrixPresets } from "@src/features/equation-editor/presets";
import { useId } from "react";
import type { Dimension } from "@src/math/types";
import { PresetOption, usePopupMenuRef } from "./PresetMenu";

export function AddMatrixControls<D extends Dimension>({
  equation,
  t,
  onAdd,
  onPreset,
  onInverse,
}: {
  equation: Equation<D>;
  t: Translate;
  onAdd: () => void;
  onPreset: (id: string) => void;
  onInverse: () => void;
}) {
  const disabled = !canAddEquationNodes(equation, "matrices");
  const inverse = analyzeEquationCapabilities(equation).inverseTransform;
  const inverseDisabledReason = getInverseDisabledReason(inverse, t);
  const inverseReasonId = useId();
  const popupMenuRef = usePopupMenuRef();
  return (
    <div
      className={`preset-split matrix-add-button matrix-add-button-${String(equation.dimension)}`}
    >
      {disabled && (
        <DiagnosticBadge
          id={`matrix-capacity-${String(equation.dimension)}`}
          messages={[t("matrixCapacityReached", { count: MAX_EQUATION_NODES })]}
          indicatorLabel={t("limitDetails")}
        />
      )}
      <button
        className="equation-add-button preset-main"
        type="button"
        onClick={onAdd}
        aria-label={`${t("addMatrix")} + M`}
        title={t("addMatrix")}
        disabled={disabled}
      >
        <math aria-hidden="true">
          <mo>+</mo>
          <mi>M</mi>
        </math>
      </button>
      <details
        ref={popupMenuRef}
        className="preset-menu popup-menu"
        onToggle={focusOpenedPopupMenu}
      >
        <summary
          className="preset-toggle"
          aria-label={t("matrixPresets")}
          title={t("matrixPresets")}
          aria-haspopup="menu"
          onKeyDown={handlePopupMenuTriggerKeyDown}
        />
        <div
          className="preset-menu-panel popup-menu-panel"
          role="menu"
          tabIndex={-1}
          aria-label={t("matrixPresets")}
          onKeyDown={handlePopupMenuKeyDown}
          onFocus={handlePopupMenuItemFocus}
        >
          <PresetOption
            label={t("inverseCurrentTransform")}
            available={inverse.kind === "available"}
            initialTabStop
            disabledReason={inverseDisabledReason}
            reasonId={inverseReasonId}
            onSelect={onInverse}
          />
          {getMatrixPresets(equation.dimension).map((preset) => (
            <button
              key={preset.id}
              type="button"
              role="menuitem"
              tabIndex={-1}
              onClick={(event) => {
                closePopupMenu(event.currentTarget);
                onPreset(preset.id);
              }}
              disabled={disabled}
            >
              {matrixPresetName(preset, t)}
            </button>
          ))}
        </div>
      </details>
    </div>
  );
}

function getInverseDisabledReason(
  capability: EquationCapabilities<Dimension>["inverseTransform"],
  t: Translate,
): string | undefined {
  switch (capability.kind) {
    case "available":
      return undefined;
    case "invalid-matrices":
      return t("inversePresetRequiresValidMatrices");
    case "unavailable":
      return t("inverseTransformUnavailable");
    case "capacity-exceeded":
      return t("inversePresetCapacityReached");
  }
}

function matrixPresetName(preset: MatrixPreset, t: Translate): string {
  if (preset.subject.kind === "axis")
    return t("reflectionAxisPreset", { axis: preset.subject.name });
  if (preset.subject.kind === "plane")
    return t("reflectionPlanePreset", { plane: preset.subject.name });
  const axis =
    preset.subject.kind === "axis-angle"
      ? ` ${t("aroundAxis", { axis: preset.subject.axis })}`
      : "";
  return t("rotationPreset", { angle: preset.subject.degrees, axis });
}
